import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {CommerceDataType} from "../common/types";
import {AppRootStateType} from "./store";
import {dataApi} from "../api/dataApi";
import {AxiosError} from "axios";
import {loadingChartsAC, setErrorAC} from "./authReducer";
import dayjs from "dayjs";
import {today} from "../common/optionsForDate";

// Типы для фильтров
export type CommerceFiltersResponseType = {
    regions: string[];
    spm: string[];
}

// Дефолтные значения для дат (стандартный период — 7 недель назад)
const startCommerceTime = dayjs(today).isoWeekday(1).subtract(6, 'week').format('YYYY-MM-DD')
const endCommerceTime = dayjs(today).isoWeekday(7).format('YYYY-MM-DD')

const sessionStartCommerceTime = sessionStorage.getItem('startCommerceTime')
const sessionEndCommerceTime = sessionStorage.getItem('endCommerceTime')

export const defaultCommerceTime = [
    sessionStartCommerceTime ? sessionStartCommerceTime : startCommerceTime,
    sessionEndCommerceTime ? sessionEndCommerceTime : endCommerceTime
]

// Специальное значение "Все" для выбора всех регионов
export const ALL_REGIONS_VALUE = 'ALL'

export type CommerceStateType = {
    commerceData: CommerceDataType[];
    commerceDataByMediaType: Record<string, CommerceDataType[]>;
    filters: CommerceFiltersResponseType;
    loadingFilters: boolean;
    error: any;
    // Options state
    commerceType: 'ecom' | 'egroc';
    mediaType: string;
    selectedRegion: string[];
    selectedSpm: string[];
    time: string[];
    ots: boolean;
    chartMode: 'line' | 'bar';
}

/** Проверяет, актуален ли ещё ответ относительно текущего UI-состояния (защита от race). */
const isCommerceRequestCurrent = (state: CommerceStateType, params: any): boolean => {
    if (params.com_type !== state.commerceType) return false

    if (params.from_date !== undefined && params.from_date !== state.time[0]) return false
    if (params.to_date !== undefined && params.to_date !== state.time[1]) return false

    if (params.spm !== undefined) {
        const currentSpm = [...state.selectedSpm].sort().join(',')
        const requestSpm = String(params.spm).split(',').filter(Boolean).sort().join(',')
        if (requestSpm !== currentSpm) return false
    }

    if (params.region !== undefined) {
        const currentRegion = state.selectedRegion.includes(ALL_REGIONS_VALUE)
            ? ''
            : state.selectedRegion.join(',')
        if (params.region !== currentRegion) return false
    }

    return true
}

export const fetchDataCommerce = createAsyncThunk<any, any, { state: AppRootStateType }>(
    'commerce/fetchDataCommerce',
    async (params: any, {dispatch, rejectWithValue, getState}) => {
        dispatch(loadingChartsAC('loading'))
        try {
            const res = await dataApi.getDataCommerce(params)
            if (!isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('succeeded'))
            return res.data
        } catch (err) {
            if (!isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('failed'))
            const error = err as AxiosError
            if (!error.response) {
                throw err
            }
            dispatch(setErrorAC(error.response.status))
            return rejectWithValue(error.response)
        }
    }
)

export const fetchDataCommerceCombined = createAsyncThunk<any, any, { state: AppRootStateType }>(
    'commerce/fetchDataCommerceCombined',
    async (params: any, {dispatch, rejectWithValue, getState}) => {
        dispatch(loadingChartsAC('loading'))
        try {
            const res = await dataApi.getDataCommerceCombined(params)
            if (!isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('succeeded'))
            return res.data
        } catch (err) {
            if (!isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('failed'))
            const error = err as AxiosError
            if (!error.response) {
                throw err
            }
            dispatch(setErrorAC(error.response.status))
            return rejectWithValue(error.response)
        }
    }
)

export const fetchDataCommercePercentageForMediaType = createAsyncThunk<any, any, { state: AppRootStateType }>(
    'commerce/fetchDataCommercePercentageForMediaType',
    async (params: any, {dispatch, rejectWithValue, getState, signal}) => {
        try {
            const res = await dataApi.getDataCommercePercentage(params)
            if (signal.aborted || !isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            return {mediaType: params.media_type, data: res.data}
        } catch (err) {
            if (signal.aborted || !isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            const error = err as AxiosError
            if (!error.response) {
                throw err
            }
            dispatch(setErrorAC(error.response.status))
            return rejectWithValue(error.response)
        }
    }
)

export const fetchFiltersCommerce = createAsyncThunk<any, any, { state: AppRootStateType }>(
    'commerce/fetchFiltersCommerce',
    async (params: any, {dispatch, rejectWithValue, getState, signal}) => {
        dispatch(loadingChartsAC('loading'))
        try {
            const res = await dataApi.getDataCommerceFilters(params)
            if (signal.aborted || params.com_type !== getState().commerce.commerceType) {
                return rejectWithValue({stale: true})
            }
            // Не ставим succeeded — спиннер графиков держим до прихода данных
            return res.data
        } catch (err) {
            if (signal.aborted || params.com_type !== getState().commerce.commerceType) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('failed'))
            const error = err as AxiosError
            if (!error.response) {
                throw err
            }
            dispatch(setErrorAC(error.response.status))
            return rejectWithValue(error.response)
        }
    }
)

export const fetchDataCommercePercentage = createAsyncThunk<any, any, { state: AppRootStateType }>(
    'commerce/fetchDataCommercePercentage',
    async (params: any, {dispatch, rejectWithValue, getState, signal}) => {
        dispatch(loadingChartsAC('loading'))
        try {
            const res = await dataApi.getDataCommercePercentage(params)
            if (signal.aborted || !isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('succeeded'))
            return res.data
        } catch (err) {
            if (signal.aborted || !isCommerceRequestCurrent(getState().commerce, params)) {
                return rejectWithValue({stale: true})
            }
            dispatch(loadingChartsAC('failed'))
            const error = err as AxiosError
            if (!error.response) {
                throw err
            }
            dispatch(setErrorAC(error.response.status))
            return rejectWithValue(error.response)
        }
    }
)

export const commerceSlice = createSlice({
    name: 'commerce',
    initialState: {
        commerceData: [],
        commerceDataByMediaType: {},
        filters: {
            regions: [],
            spm: []
        },
        loadingFilters: false,
        error: null,
        // Options state
        commerceType: 'ecom',
        mediaType: 'tv',
        selectedRegion: [ALL_REGIONS_VALUE],
        selectedSpm: [],
        time: defaultCommerceTime,
        ots: false,
        chartMode: 'line'
    } as CommerceStateType,

    reducers: {
        setCommerceTypeAC(state, action: PayloadAction<'ecom' | 'egroc'>) {
            state.commerceType = action.payload
            // Сбрасываем выбор и данные, чтобы устаревшие ответы прошлого типа не портили графики
            state.selectedSpm = []
            state.commerceData = []
            state.commerceDataByMediaType = {}
        },
        setMediaTypeAC(state, action: PayloadAction<string>) {
            state.mediaType = action.payload
        },
        setSelectedRegionAC(state, action: PayloadAction<string[]>) {
            state.selectedRegion = action.payload
        },
        setSelectedSpmAC(state, action: PayloadAction<string[]>) {
            state.selectedSpm = action.payload
        },
        setCommerceTimeAC(state, action: PayloadAction<string[]>) {
            state.time = action.payload
        },
        setOtsAC(state, action: PayloadAction<boolean>) {
            state.ots = action.payload
        },
        resetCommerceTimeAC(state) {
            state.time = defaultCommerceTime
        },
        initSelectedSpmAC(state, action: PayloadAction<string[]>) {
            // Всегда устанавливаем все супермаркеты
            state.selectedSpm = action.payload
        },
        setChartModeAC(state, action: PayloadAction<'line' | 'bar'>) {
            state.chartMode = action.payload
        }
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchDataCommerce.fulfilled, (state, action) => {
                if (!isCommerceRequestCurrent(state, action.meta.arg)) return
                state.commerceData = action.payload
            })
            .addCase(fetchDataCommerce.rejected, (state, action: any) => {
                if (action.payload?.stale || action.meta?.aborted) return
                state.error = action.payload?.status
            })
            .addCase(fetchDataCommerceCombined.fulfilled, (state, action) => {
                if (!isCommerceRequestCurrent(state, action.meta.arg)) return
                state.commerceData = action.payload
            })
            .addCase(fetchDataCommerceCombined.rejected, (state, action: any) => {
                if (action.payload?.stale || action.meta?.aborted) return
                state.error = action.payload?.status
            })
            .addCase(fetchDataCommercePercentage.fulfilled, (state, action) => {
                if (!isCommerceRequestCurrent(state, action.meta.arg)) return
                state.commerceData = action.payload
            })
            .addCase(fetchDataCommercePercentage.rejected, (state, action: any) => {
                if (action.payload?.stale || action.meta?.aborted) return
                state.error = action.payload?.status
            })
            .addCase(fetchDataCommercePercentageForMediaType.fulfilled, (state, action) => {
                if (!isCommerceRequestCurrent(state, action.meta.arg)) return
                state.commerceDataByMediaType[action.payload.mediaType] = action.payload.data
            })
            .addCase(fetchDataCommercePercentageForMediaType.rejected, (state, action: any) => {
                if (action.payload?.stale || action.meta?.aborted) return
                state.error = action.payload?.status
            })
            .addCase(fetchFiltersCommerce.pending, (state) => {
                state.loadingFilters = true
            })
            .addCase(fetchFiltersCommerce.fulfilled, (state, action) => {
                if (action.meta.arg.com_type !== state.commerceType) return
                state.filters = action.payload
                state.loadingFilters = false
            })
            .addCase(fetchFiltersCommerce.rejected, (state, action: any) => {
                if (action.payload?.stale || action.meta?.aborted) return
                state.error = action.payload?.status
                state.loadingFilters = false
            })
    }
})

export const {
    setCommerceTypeAC,
    setMediaTypeAC,
    setSelectedRegionAC,
    setSelectedSpmAC,
    setCommerceTimeAC,
    setOtsAC,
    resetCommerceTimeAC,
    initSelectedSpmAC,
    setChartModeAC
} = commerceSlice.actions

export const commerceReducer = commerceSlice.reducer

